import {
  FormControl,
  FormLabel,
  TextField,
  Stack,
  Button,
  Typography,
  Menu,
  MenuItem,
  SvgIcon,
} from '@mui/material'
import { IconChevronDown } from '@tabler/icons-react'
import { useState } from 'react'

type FilterOperator = 'equals' | 'lessThan' | 'greaterThan'

type FilterNumberDefaultValue = {
  value: number
  operator: FilterOperator
}

type FilterNumberProps = {
  label: string
  defaultValue?: FilterNumberDefaultValue
  onSubmit: (params: {
    value: number
    label: string
    operator: FilterOperator
    setter: (value: number) => boolean
  }) => void
  onClose: () => void
  placeholder?: string
}

const operatorLabels: Record<FilterOperator, string> = {
  equals: 'Equals',
  lessThan: 'Less than',
  greaterThan: 'Greater than',
}

const createSetter = (value: number, operator: FilterOperator): ((v: number) => boolean) => {
  switch (operator) {
    case 'equals':
      return (v: number) => v === value
    case 'lessThan':
      return (v: number) => v < value
    case 'greaterThan':
      return (v: number) => v > value
  }
}

const createLabel = (value: number, operator: FilterOperator): string => {
  switch (operator) {
    case 'equals':
      return `= ${value}`
    case 'lessThan':
      return `< ${value}`
    case 'greaterThan':
      return `> ${value}`
  }
}

export const FilterNumber = ({
  label,
  defaultValue,
  onSubmit,
  onClose,
  placeholder,
}: FilterNumberProps) => {
  const [value, setValue] = useState<string>(defaultValue?.value?.toString() ?? '')
  const [operator, setOperator] = useState<FilterOperator>(defaultValue?.operator ?? 'equals')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }

  const handleOperatorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleOperatorSelect = (selectedOperator: FilterOperator) => {
    setOperator(selectedOperator)
    setAnchorEl(null)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleSubmit = () => {
    const numValue = parseFloat(value)
    if (!isNaN(numValue)) {
      onSubmit({
        value: numValue,
        label: createLabel(numValue, operator),
        operator,
        setter: createSetter(numValue, operator),
      })
    }
  }

  const handleClose = () => {
    onClose()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && value.trim() !== '') {
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) {
        handleSubmit()
      }
    }
  }

  const isValidNumber = value.trim() !== '' && !isNaN(parseFloat(value))

  return (
    <Stack sx={{ py: 2, px: 2 }}>
      <FormControl component="fieldset" sx={{ gap: 1, flexDirection: 'column' }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <FormLabel component="legend">
            <Typography variant="subtitle2">{label}</Typography>
          </FormLabel>
          <Button
            size="small"
            onClick={handleOperatorClick}
            sx={{ textTransform: 'none' }}
            endIcon={
              <SvgIcon fontSize="small">
                <IconChevronDown />
              </SvgIcon>
            }
          >
            {operatorLabels[operator]}
          </Button>
        </Stack>

        <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
          {(Object.keys(operatorLabels) as FilterOperator[]).map((op) => (
            <MenuItem key={op} selected={op === operator} onClick={() => handleOperatorSelect(op)}>
              <Typography variant="caption">{operatorLabels[op]}</Typography>
            </MenuItem>
          ))}
        </Menu>

        <TextField
          type="number"
          size="small"
          value={value}
          onChange={handleValueChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus
          sx={{ mt: 1 }}
        />

        <Stack direction="row" spacing={1} useFlexGap sx={{ alignSelf: 'flex-end', mt: 1 }}>
          <Button variant="contained" size="small" onClick={handleSubmit} disabled={!isValidNumber}>
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      </FormControl>
    </Stack>
  )
}
